import React, { useCallback, useEffect, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  AlertCircle,
  Building2,
  CreditCard,
  Globe,
  Info,
  Link,
  MessageSquare,
  Palette,
  Save,
  Users,
  Zap,
} from "lucide-react";
import { toast } from "sonner";
import { updateNativeClientPlatform } from "../../../services/native-client-platform";
import { getNativeClientPlatformPlans } from "../../../services/native-client-platform-plan";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Switch } from "../../../components/ui/switch";
import { Select } from "../../../components/ui/select";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "../../../components/ui/card";
import { Badge } from "../../../components/ui/badge";
import { Spinner } from "../../../components/ui/spinner";
import {
  FormField,
  FormItem,
  FormLabel,
  FormDescription,
  FormMessage,
} from "../../../components/ui/form-field";
import { clientPlatformSchema } from "./schema";
import { cn } from "../../../lib/cn";

export default function GeneralForm({ data, setData }) {
  const [updating, setUpdating] = useState(false);
  const [plans, setPlans] = useState([]);
  const [plansLoading, setPlansLoading] = useState(false);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isDirty },
  } = useForm({
    resolver: zodResolver(clientPlatformSchema),
    defaultValues: {
      name: data?.name || "",
      subdomain: data?.subdomain || "",
      customURL: data?.customURL || "",
      primaryColor: data?.primaryColor || "#AD34FE",
      logo: data?.logo || "",
      isActive: data?.isActive ?? true,
      nativeClientPlatformPlanID: data?.nativeClientPlatformPlanID ?? null,
    },
    mode: "onBlur",
  });

  const selectedPlanID = useWatch({ control, name: "nativeClientPlatformPlanID" });

  useEffect(() => {
    if (!data?.companyID) return;
    setPlansLoading(true);
    getNativeClientPlatformPlans({ companyID: data.companyID })
      .then((response) => {
        const list = Array.isArray(response) ? response : response.data || [];
        setPlans(list);
      })
      .catch((err) => {
        console.error("Failed to fetch plans", err);
        toast.error("Não foi possível carregar os planos");
      })
      .finally(() => setPlansLoading(false));
  }, [data?.companyID]);

  useEffect(() => {
    reset({
      name: data?.name || "",
      subdomain: data?.subdomain || "",
      customURL: data?.customURL || "",
      primaryColor: data?.primaryColor || "#AD34FE",
      logo: data?.logo || "",
      isActive: data?.isActive ?? true,
      nativeClientPlatformPlanID: data?.nativeClientPlatformPlanID ?? null,
    });
  }, [data, reset]);

  const onSubmit = useCallback(
    (values) => {
      setUpdating(true);
      updateNativeClientPlatform(data.id, { ...data, ...values })
        .then(({ data: updated }) => {
          setData(updated);
          toast.success("Plataforma atualizada com sucesso");
        })
        .catch((e) => {
          console.error(e);
          toast.error("Falha ao atualizar plataforma");
        })
        .finally(() => setUpdating(false));
    },
    [data, setData]
  );

  const selectedPlan = plans.find((p) => p.id === selectedPlanID);

  const planOptions = [
    { value: null, label: "Sem plano" },
    ...plans.map((p) => ({
      value: p.id,
      label: p.name,
    })),
  ];

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-6 pb-24">
      {/* Identidade */}
      <SectionCard
        icon={Building2}
        title="Identidade"
        description="Nome, domínio, aparência e estado da plataforma."
      >
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <FormField
            control={control}
            name="name"
            render={({ field, error }) => (
              <FormItem>
                <FormLabel htmlFor="name" required>
                  Nome da plataforma
                </FormLabel>
                <Input
                  id="name"
                  placeholder="Ex: Portal do cliente"
                  invalid={!!error}
                  {...field}
                />
                <FormMessage error={error} />
              </FormItem>
            )}
          />
          <FormField
            control={control}
            name="subdomain"
            render={({ field, error }) => (
              <FormItem>
                <FormLabel htmlFor="subdomain">Subdomínio</FormLabel>
                <Input
                  id="subdomain"
                  placeholder="Ex: minha-empresa"
                  leftIcon={Globe}
                  disabled
                  {...field}
                  value={field.value || ""}
                />
                <FormDescription>Em breve</FormDescription>
                <FormMessage error={error} />
              </FormItem>
            )}
          />
          <FormField
            control={control}
            name="customURL"
            render={({ field, error }) => (
              <FormItem>
                <FormLabel htmlFor="customURL">URL personalizada</FormLabel>
                <Input
                  id="customURL"
                  placeholder="https://chat.minha-empresa.com"
                  leftIcon={Link}
                  disabled
                  {...field}
                  value={field.value || ""}
                />
                <FormDescription>Em breve</FormDescription>
                <FormMessage error={error} />
              </FormItem>
            )}
          />
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-6">
          <FormField
            control={control}
            name="primaryColor"
            render={({ field }) => (
              <div className="flex items-center gap-3">
                <div className="flex flex-col gap-0.5">
                  <FormLabel>
                    <span className="flex items-center gap-1.5">
                      <Palette size={12} />
                      Cor primária
                    </span>
                  </FormLabel>
                  <FormDescription>Aparência do chat</FormDescription>
                </div>
                <div className="relative">
                  <input
                    type="color"
                    value={field.value || "#AD34FE"}
                    onChange={(e) => field.onChange(e.target.value)}
                    className="h-9 w-12 cursor-pointer rounded-lg border-none bg-transparent"
                    style={{
                      outline: "none",
                    }}
                  />
                </div>
                <span
                  className="rounded-md px-2 py-1 font-mono text-xs"
                  style={{
                    background: "var(--dash-surface-subtle)",
                    border: "1px solid var(--dash-border)",
                    color: "var(--dash-text-muted)",
                  }}
                >
                  {field.value || "#AD34FE"}
                </span>
              </div>
            )}
          />

          <FormField
            control={control}
            name="isActive"
            render={({ field }) => (
              <ToggleField
                label="Ativa"
                description="Controla se a plataforma está acessível"
                checked={!!field.value}
                onChange={field.onChange}
              />
            )}
          />
        </div>
      </SectionCard>

      {/* Plano */}
      <SectionCard
        icon={CreditCard}
        title="Plano"
        description="Selecione o plano associado a esta plataforma."
      >
        {plansLoading ? (
          <div className="flex items-center justify-center py-8">
            <Spinner size={18} />
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            <FormField
              control={control}
              name="nativeClientPlatformPlanID"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Plano da plataforma</FormLabel>
                  <Select
                    value={field.value}
                    onChange={field.onChange}
                    options={planOptions}
                    placeholder="Selecione um plano"
                  />
                </FormItem>
              )}
            />

            {selectedPlan && (
              <div
                className="flex flex-col gap-4 rounded-xl p-5"
                style={{
                  background: "var(--dash-surface-subtle)",
                  border: "1px solid var(--dash-border)",
                }}
              >
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2">
                    <CreditCard
                      size={16}
                      style={{ color: "var(--dash-primary)" }}
                    />
                    <span
                      className="text-sm font-semibold"
                      style={{ color: "var(--dash-text)" }}
                    >
                      {selectedPlan.name}
                    </span>
                  </div>
                  <span
                    className="text-lg font-bold"
                    style={{ color: "var(--dash-primary)" }}
                  >
                    {selectedPlan.price != null
                      ? `R$ ${Number(selectedPlan.price).toFixed(2)}`
                      : "Grátis"}
                  </span>
                </div>

                <div
                  className="h-px w-full"
                  style={{ background: "var(--dash-border)" }}
                />

                <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                  {selectedPlan.maxMessages != null && (
                    <LimitItem
                      icon={MessageSquare}
                      label="Mensagens"
                      value={selectedPlan.maxMessages.toLocaleString("pt-BR")}
                    />
                  )}
                  {selectedPlan.maxUsers != null && (
                    <LimitItem
                      icon={Users}
                      label="Usuários"
                      value={selectedPlan.maxUsers.toLocaleString("pt-BR")}
                    />
                  )}
                  {selectedPlan.maxSessions != null && (
                    <LimitItem
                      icon={Zap}
                      label="Sessões"
                      value={selectedPlan.maxSessions.toLocaleString("pt-BR")}
                    />
                  )}
                </div>

                {selectedPlan.features && selectedPlan.features.length > 0 && (
                  <div className="flex flex-wrap gap-2 pt-1">
                    {selectedPlan.features.map((feature, i) => (
                      <Badge key={i} variant="primary">
                        {feature}
                      </Badge>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </SectionCard>

      {/* Sticky save bar */}
      <div
        className="fixed bottom-0 right-0 z-20 border-t backdrop-blur-md"
        style={{
          left: "var(--sidebar-width, 56px)",
          background: "color-mix(in srgb, var(--dash-bg) 92%, transparent)",
          borderColor: "var(--dash-border)",
        }}
      >
        <div className="mx-auto flex w-full max-w-[1400px] items-center justify-between gap-4 px-6 py-4">
          <div className="flex items-center gap-2">
            {Object.keys(errors).length > 0 ? (
              <>
                <AlertCircle size={14} style={{ color: "#d61f3f" }} />
                <span
                  className="text-xs font-medium"
                  style={{ color: "#d61f3f" }}
                >
                  Existem campos com erro
                </span>
              </>
            ) : isDirty ? (
              <>
                <Info size={14} style={{ color: "var(--dash-primary)" }} />
                <span
                  className="text-xs font-medium"
                  style={{ color: "var(--dash-primary)" }}
                >
                  Você tem alterações não salvas
                </span>
              </>
            ) : (
              <span
                className="text-xs"
                style={{ color: "var(--dash-text-muted)" }}
              >
                Tudo sincronizado
              </span>
            )}
          </div>
          <Button
            type="submit"
            variant="primary"
            size="lg"
            leftIcon={Save}
            loading={updating}
            disabled={updating}
          >
            Salvar
          </Button>
        </div>
      </div>
    </form>
  );
}

function SectionCard({ icon: Icon, title, description, badge, action, children }) {
  return (
    <Card className="overflow-visible">
      <CardHeader className="flex-row items-start justify-between gap-4 pb-3">
        <div className="flex items-start gap-3">
          {Icon && (
            <div
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl"
              style={{
                background: "var(--dash-primary-soft)",
                border: "1px solid var(--dash-primary-border)",
                color: "var(--dash-primary)",
              }}
            >
              <Icon size={16} strokeWidth={2.2} />
            </div>
          )}
          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-2">
              <CardTitle>{title}</CardTitle>
              {badge}
            </div>
            {description && <CardDescription>{description}</CardDescription>}
          </div>
        </div>
        {action}
      </CardHeader>
      <CardContent className="pt-2">{children}</CardContent>
    </Card>
  );
}

function ToggleField({ label, description, icon: Icon, checked, onChange, disabled }) {
  return (
    <div className="flex items-center gap-3">
      <Switch checked={checked} onChange={onChange} disabled={disabled} />
      <div className="flex flex-col gap-0.5">
        <span
          className="flex items-center gap-1.5 text-xs font-semibold"
          style={{ color: "var(--dash-text)" }}
        >
          {Icon && <Icon size={12} />}
          {label}
        </span>
        {description && (
          <span
            className="text-[11px]"
            style={{ color: "var(--dash-text-muted)" }}
          >
            {description}
          </span>
        )}
      </div>
    </div>
  );
}

function LimitItem({ icon: Icon, label, value }) {
  return (
    <div
      className="flex items-center gap-3 rounded-lg px-3 py-2.5"
      style={{
        background: "var(--dash-surface)",
        border: "1px solid var(--dash-border)",
      }}
    >
      <Icon
        size={15}
        strokeWidth={2}
        style={{ color: "var(--dash-text-muted)" }}
      />
      <div className="flex flex-col">
        <span
          className="text-[10px] font-medium uppercase tracking-[0.12em]"
          style={{ color: "var(--dash-text-muted)" }}
        >
          {label}
        </span>
        <span
          className="text-sm font-semibold tabular-nums"
          style={{ color: "var(--dash-text)" }}
        >
          {value}
        </span>
      </div>
    </div>
  );
}
